export type BrowserFamily = "chrome" | "edge" | "safari" | "firefox" | "other";
export type ClientSurface = "desktop_web" | "mobile_web" | "mobile_app";
export type FormFactor = "desktop" | "phone" | "tablet";
export type ClientOperatingSystem = "ios" | "android" | "macos" | "windows" | "chrome_os" | "linux" | "other";

export interface ClientEnvironment {
  browserFamily: BrowserFamily;
  clientSurface: ClientSurface;
  formFactor: FormFactor;
  operatingSystem: ClientOperatingSystem;
}

export interface ClientEnvironmentDetectionInput {
  userAgent?: string;
  platform?: string;
  maxTouchPoints?: number;
  screenWidth?: number;
  screenHeight?: number;
  viewportWidth?: number;
  viewportHeight?: number;
  isRunningAsMobileApp?: boolean;
  nativeOperatingSystem?: "ios" | "android";
}

function getBrowserFamily(userAgent: string): BrowserFamily {
  if (/EdgA|EdgiOS|Edg\//.test(userAgent)) return "edge";
  if (/FxiOS|Firefox/.test(userAgent)) return "firefox";
  if (/CriOS|Chrome/.test(userAgent)) return "chrome";
  if (/Safari/.test(userAgent)) return "safari";
  return "other";
}

function getOperatingSystem(
  userAgent: string,
  platform: string,
  maxTouchPoints: number,
  nativeOperatingSystem?: "ios" | "android"
): ClientOperatingSystem {
  if (nativeOperatingSystem) return nativeOperatingSystem;
  const isIPadRequestingDesktopSite = platform === "MacIntel" && maxTouchPoints > 1;
  if (/iPad|iPhone|iPod/.test(userAgent) || isIPadRequestingDesktopSite) return "ios";
  if (/Android/.test(userAgent)) return "android";
  if (/CrOS/.test(userAgent)) return "chrome_os";
  if (/Mac/.test(platform) || /Mac OS X/.test(userAgent)) return "macos";
  if (/Win/.test(platform) || /Windows/.test(userAgent)) return "windows";
  if (/Linux/.test(platform) || /Linux/.test(userAgent)) return "linux";
  return "other";
}

function getFormFactor(
  userAgent: string,
  platform: string,
  maxTouchPoints: number,
  screenWidth: number,
  screenHeight: number,
  operatingSystem: ClientOperatingSystem,
  isRunningAsMobileApp: boolean
): FormFactor {
  const isIPadRequestingDesktopSite = platform === "MacIntel" && maxTouchPoints > 1;
  const explicitlyTablet = /iPad|Tablet|Kindle|Silk/.test(userAgent) || isIPadRequestingDesktopSite;
  const androidTablet = operatingSystem === "android" && !/Mobile/.test(userAgent);
  const minDimension = Math.min(screenWidth, screenHeight);
  const maxDimension = Math.max(screenWidth, screenHeight);
  const mobileOsTabletSize =
    (operatingSystem === "ios" || operatingSystem === "android" || isRunningAsMobileApp) &&
    minDimension >= 600 &&
    maxDimension <= 1400;

  if (explicitlyTablet || androidTablet || mobileOsTabletSize) return "tablet";
  if (
    operatingSystem === "ios" ||
    operatingSystem === "android" ||
    isRunningAsMobileApp ||
    /Mobile|Phone/.test(userAgent)
  ) {
    return "phone";
  }
  return "desktop";
}

export function detectClientEnvironment(input: ClientEnvironmentDetectionInput = {}): ClientEnvironment {
  const userAgent = input.userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
  const platform = input.platform ?? (typeof navigator !== "undefined" ? navigator.platform : "");
  const maxTouchPoints =
    input.maxTouchPoints ?? (typeof navigator !== "undefined" ? (navigator.maxTouchPoints ?? 0) : 0);
  const screenWidth =
    input.screenWidth ?? (typeof window !== "undefined" ? window.screen?.width || window.innerWidth || 1440 : 1440);
  const screenHeight =
    input.screenHeight ?? (typeof window !== "undefined" ? window.screen?.height || window.innerHeight || 900 : 900);
  const hasNativeWebViewBridge = typeof window !== "undefined" && Boolean(window.ReactNativeWebView);
  // The native bridge is definitive. The Zustand handshake can still be false
  // during the first render while the WebView's INIT_APP reply is in flight.
  const isRunningAsMobileApp = Boolean(input.isRunningAsMobileApp || hasNativeWebViewBridge);
  const operatingSystem = getOperatingSystem(userAgent, platform, maxTouchPoints, input.nativeOperatingSystem);
  const formFactor = getFormFactor(
    userAgent,
    platform,
    maxTouchPoints,
    screenWidth,
    screenHeight,
    operatingSystem,
    isRunningAsMobileApp
  );

  return {
    browserFamily: getBrowserFamily(userAgent),
    clientSurface: isRunningAsMobileApp ? "mobile_app" : formFactor === "desktop" ? "desktop_web" : "mobile_web",
    formFactor,
    operatingSystem,
  };
}

interface SentryEventLike {
  tags?: Record<string, unknown>;
  contexts?: Record<string, Record<string, unknown> | undefined>;
}

export function addClientEnvironmentToSentryEvent<T extends SentryEventLike>(
  event: T,
  input: ClientEnvironmentDetectionInput = {}
): T {
  const environment = detectClientEnvironment(input);
  const viewportWidth = input.viewportWidth ?? (typeof window !== "undefined" ? window.innerWidth : undefined);
  const viewportHeight = input.viewportHeight ?? (typeof window !== "undefined" ? window.innerHeight : undefined);

  return {
    ...event,
    tags: {
      ...event.tags,
      client_surface: environment.clientSurface,
      form_factor: environment.formFactor,
      operating_system: environment.operatingSystem,
      browser_family: environment.browserFamily,
    },
    contexts: {
      ...event.contexts,
      client_environment: {
        type: "client_environment",
        client_surface: environment.clientSurface,
        form_factor: environment.formFactor,
        operating_system: environment.operatingSystem,
        browser_family: environment.browserFamily,
        ...(viewportWidth === undefined ? {} : { viewport_width: viewportWidth }),
        ...(viewportHeight === undefined ? {} : { viewport_height: viewportHeight }),
      },
    },
  } as T;
}
